import { lightTheme, darkTheme } from '@devnw/theme'
import type { ExtendedTheme, SpyderTheme } from '@devnw/theme'

/**
 * Parse an ExtendedTheme spacing token ('12px') into the NUMBER SpyderTheme
 * requires. AppShell passes theme.spacing.* straight into a react-native
 * StyleSheet, where a string would be mis-handled — this conversion is the
 * whole reason the two theme systems cannot share one object.
 */
const px = (value: string | number | undefined, fallback: number): number => {
  if (typeof value === 'number') return value
  if (typeof value !== 'string') return fallback
  const n = Number.parseFloat(value)
  return Number.isFinite(n) ? n : fallback
}

/**
 * Derive the SpyderTheme that @devnw/layout's AppShell consumes from the
 * ExtendedTheme this app already builds with ThemeBuilder.
 *
 * The two are separate, non-interoperable interfaces from the same package:
 * ExtendedTheme has {bg, border, spacing, scales} and brand.brandText;
 * SpyderTheme has a flat `colors` set, numeric `spacing`, a `radius` token
 * scale, and its own brand.name. @devnw/theme ships no converter, so this is
 * ours.
 *
 * Only five fields are load-bearing today (AppShell reads colors.background,
 * colors.surface, colors.border, spacing.sm, spacing.md), but the whole
 * SpyderTheme shape must be satisfied — so start from the stock theme for the
 * fields the app has no opinion about, and override the ones it does. That
 * keeps the shell visually consistent with the app it wraps instead of
 * silently reverting to stock white.
 *
 * MINOR-6 correction (final-review fix wave): two fields below are NOT
 * bridged, both falling straight through to the stock theme's own values —
 * noted here explicitly so neither is mistaken for something this function
 * derives from the app:
 *  - `brand.name` — ExtendedTheme carries no name of its own (only
 *    `brand.brandText`, a colour token, not a name) for this function to
 *    read, so the output keeps the stock theme's literal "SPYDER" default
 *    rather than anything app-specific.
 *  - `radius` — SpyderTheme's `radius` is a whole token SCALE
 *    (`{xs,sm,md,lg,xl,pill,full}`), structurally unrelated to
 *    ExtendedTheme's single `border.radius` number (set to `8` via the
 *    `radius: 8` passed into `ThemeBuilder.fromPalette` in index.tsx). This
 *    function never reads `border.radius`, so the output keeps the stock
 *    scale untouched. Harmless today — AppShell doesn't read `radius` — but
 *    the next @devnw/layout component adopted that DOES read it will
 *    silently get stock corners instead of this app's configured radius
 *    until this function is extended to bridge it.
 */
export const toSpyderTheme = (theme: ExtendedTheme): SpyderTheme => {
  const base = theme.variant === 'dark' ? darkTheme : lightTheme
  // ExtendedTheme.spacing is a required, fully-typed
  // Record<'xxs'|...|'xxl', string> (px strings) — no cast needed to read it.
  const { spacing } = theme

  return {
    ...base,
    variant: theme.variant,
    brand: {
      ...base.brand,
      logos: theme.brand?.logos ?? base.brand.logos,
      font: theme.brand?.font ?? base.brand.font,
    },
    colors: {
      ...base.colors,
      primary: theme.colors.primary,
      secondary: theme.colors.secondary,
      success: theme.colors.success,
      warning: theme.colors.warning,
      danger: theme.colors.danger,
      // The five AppShell actually reads:
      background: theme.bg.canvas,
      surface: theme.bg.surface,
      border: theme.border.color,
    },
    spacing: {
      xxs: px(spacing.xxs, base.spacing.xxs),
      xs: px(spacing.xs, base.spacing.xs),
      sm: px(spacing.sm, base.spacing.sm),
      md: px(spacing.md, base.spacing.md),
      lg: px(spacing.lg, base.spacing.lg),
      xl: px(spacing.xl, base.spacing.xl),
      xxl: px(spacing.xxl, base.spacing.xxl),
      // ExtendedTheme.spacing has no xxxl — keep the stock step.
      xxxl: base.spacing.xxxl,
    },
  }
}
